import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { AgentsDataProvider, ActivityContentResponse } from '@trovata/app/features/ai-agents/services/agents-data-provider';
import { CashAgentsDataProvider } from '@trovata/app/features/ai-agents/services/cash-agents-data.provider';
import { TmsAgentsDataProvider } from '@trovata/app/features/ai-agents/services/tms-agents-data.provider';
import { AgentsState } from '@trovata/app/features/ai-agents/store/state/agents.state';
import {
	Agent,
	AgentDetailResponse,
	AgentInvocationsResponse,
	AgentsResponse,
	InvokeAgentResponse,
	PatchAgentBody,
} from '@trovata/app/features/ai-agents/models/agents.model';

/**
 * AgentsDataProviderRouter — the app-wide runtime-routing data provider.
 *
 * `AgentsState` is a root singleton (`provideStore(TROVATA_APP_STATES,…)`), so a
 * `/tms` route-level provider override would never reach it. Instead, the root
 * `AGENTS_DATA_PROVIDER` token resolves to THIS router, which dispatches each
 * call to the right backend at runtime:
 *
 * - Active context is TMS (`/tms/*` route) → `TmsAgentsDataProvider`
 *   (legacy ATOM `/html/ai-data`).
 * - Otherwise (Cash, the default) → `CashAgentsDataProvider`, a transparent
 *   pass-through to the existing `AgentsHttpService`.
 *
 * Cash isolation: Cash & TMS are mutually-exclusive sibling routes. On any
 * non-`/tms` URL this router is a pure pass-through to the unchanged Cash
 * provider, so the Cash agent path is provably unaffected — the only added
 * surface is one runtime branch that defaults to Cash.
 *
 * Spec lock: `documentation/implementation/system/tms-agents-migration.md`
 * §4 ("DI correction") + §5 P3.
 */
@Injectable({ providedIn: 'root' })
export class AgentsDataProviderRouter implements AgentsDataProvider {
	private readonly router: Router = inject(Router);
	private readonly store: Store = inject(Store);
	private readonly cashProvider: CashAgentsDataProvider = inject(CashAgentsDataProvider);
	private readonly tmsProvider: TmsAgentsDataProvider = inject(TmsAgentsDataProvider);

	/** Route prefix that identifies the TMS product app. */
	private static readonly TMS_ROUTE_PREFIX: string = '/tms';

	createAgent(): Observable<Agent> {
		return this.active().createAgent();
	}

	listAgents(): Observable<AgentsResponse> {
		return this.active().listAgents();
	}

	getAgentDetail(agentId: string): Observable<AgentDetailResponse> {
		return this.active().getAgentDetail(agentId);
	}

	updateAgent(request: PatchAgentBody): Observable<AgentsResponse> {
		return this.active().updateAgent(request);
	}

	deleteAgent(agentId: string): Observable<void> {
		return this.active().deleteAgent(agentId);
	}

	invokeAgent(agentId: string): Observable<InvokeAgentResponse> {
		return this.active().invokeAgent(agentId);
	}

	/** Both bounds are forwarded: a router that accepted only `agentIds` silently discarded the
	 *  `createdAfter` any consumer of the shared token is entitled to pass (CR-NG-007). */
	getRecentInvocations(agentIds?: readonly string[], createdAfter?: string): Observable<AgentInvocationsResponse> {
		return this.active().getRecentInvocations(agentIds, createdAfter);
	}

	/**
	 * Delegate the optional activity-content fetch to the active provider. Only TMS implements it
	 * (the full run report lives in ATOM file storage); the guard in `SelectActivity` only calls
	 * this when an entry has a `rawFileId` (TMS-only), so Cash never reaches the fallback.
	 */
	getActivityContent(rawFileId: string): Observable<ActivityContentResponse> {
		const provider: AgentsDataProvider = this.active();
		return provider.getActivityContent ? provider.getActivityContent(rawFileId) : of({ content: '' });
	}

	/**
	 * Resolves the provider for the current context. Prefers the EXPLICIT context declared by the active
	 * surface (`AgentsState.explicitContext`, set by the panel from its `context` input) — the reliable
	 * SSOT — over the `router.url` inference, because routed components mount before `NavigationEnd`
	 * settles the URL, so a URL-only check races on a hard refresh / SPA nav into `/tms` and would route
	 * to the wrong backend. Falls back to `router.url` (then Cash) when no surface has declared a context.
	 */
	private active(): AgentsDataProvider {
		const explicit: 'cash' | 'tms' | null = this.store.selectSnapshot(AgentsState.explicitContext);
		const isTms: boolean = explicit !== null ? explicit === 'tms' : this.router.url.startsWith(AgentsDataProviderRouter.TMS_ROUTE_PREFIX);
		return isTms ? this.tmsProvider : this.cashProvider;
	}
}
